import React, { useState, useEffect, useRef } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { query, collection, where, getDocs } from 'firebase/firestore';
import Dashboard from '../pages/Dashboard';
import { db } from '../config/firebase'; // Assuming you have a Firebase config file
import Loader from '../components/Loader';
import fetchData from '../utils/fetchData';

/*
 * The key ProtectedRoute and the uptime deep links use for a FULL in-app path.
 * redirectHandler keeps its own key for its short `?redirect=plans` map values:
 * the two used to share `redirectAfterLogin`, so each could read the other's
 * value — and redirectHandler wiped it 500ms after its own navigation, which is
 * how an uptime deep link got lost on a slow status check.
 */
export const PATH_REDIRECT_KEY = 'redirectAfterLogin';

/** Only an in-app absolute path, never a protocol-relative or external URL. */
const safeInAppPath = (p) => (typeof p === 'string' && p.startsWith('/') && !p.startsWith('//') ? p : null);

/**
 * A path worth returning to after sign-in. The bare dashboard and Statistics
 * are where sign-in lands anyway — keeping them would only cost the landing
 * its ?refreshCredits.
 */
export const keepsDeepLink = (p) => {
    const path = safeInAppPath(p);
    if (!path) return false;
    const bare = path.split(/[?#]/)[0].replace(/\/+$/, '');
    return bare.startsWith('/dashboard/') && bare !== '/dashboard/statistics';
};

/*
 * A link kept from a signed-out visit is for THAT sign-in, so it is stamped: one
 * abandoned at the login page must not hijack a sign-in hours later (one headed
 * for Plans from a ?redirect=plans email, say). A plain path — the uptime links
 * — carries no stamp and is read as before.
 */
const DEEP_LINK_TTL_MS = 60 * 60 * 1000;
export const keepPath = (path) => JSON.stringify({ path, at: Date.now() });
export const keptPath = (raw) => {
    if (!raw || raw[0] !== '{') return raw;
    try {
        const { path, at } = JSON.parse(raw);
        return Date.now() - Number(at) < DEEP_LINK_TTL_MS ? path : null;
    } catch (_) {
        return null;
    }
};

/** fetchData throws `HTTP <status>: <body>`; the status is all this needs. */
const statusOf = (err) => {
    const m = /HTTP (\d{3})/.exec(String(err?.message || ''));
    return m ? Number(m[1]) : 0;
};

const wait = (ms) => new Promise((r) => { setTimeout(r, ms); });

const ProtectedRoute = ({ element, ...props }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isUserBlocked, setIsUserBlocked] = useState(false);
    const location = useLocation();
    // Set once this route has let someone in. Signing out from inside the app is
    // not a deep link to come back to: the next person to sign in on this
    // browser would land on the last one's page.
    const wasInRef = useRef(false);

    useEffect(() => {
        const auth = getAuth();
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            setIsLoading(true); // Start loading state
            if (user) {
                try {
                    // Check if the user exists in the Firestore `users` collection
                    // const usersQuery = query(
                    //     collection(db, "users"),
                    //     where("email", "==", user.email)
                    // );
                    // const querySnapshot = await getDocs(usersQuery);

                    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}ai/writer/v1/user/firebase/${user.uid}`;

                    /*
                     * A FAILED CHECK IS NOT A SIGN-OUT. fetchData throws on any
                     * non-2xx and the catch only logged, so isAuthenticated
                     * stayed false and a signed-in user was bounced to the login
                     * page — losing their deep link — whenever this one call hit
                     * a 5xx or a dropped connection. Now: one retry on a
                     * transient failure, then let them in. Only an explicit
                     * `isBlocked` keeps anyone out; a 404 is "no record yet",
                     * which has always been allowed.
                     */
                    let response = null;
                    for (let attempt = 0; attempt < 2; attempt += 1) {
                        try {
                            // eslint-disable-next-line no-await-in-loop
                            response = await fetchData({ url: apiUrl });
                            break;
                        } catch (err) {
                            const st = statusOf(err);
                            if (st === 404 || (st >= 400 && st < 500)) break;
                            // eslint-disable-next-line no-await-in-loop
                            if (attempt === 0) await wait(1000);
                        }
                    }

                    if (response?.user?.isBlocked) {
                        setIsUserBlocked(true);
                        await signOut(auth);
                        setIsAuthenticated(false);
                    } else {
                        wasInRef.current = true;
                        setIsUserBlocked(false);
                        setIsAuthenticated(true);
                    }
                } catch (error) {
                    console.error("Error checking user status:", error);
                    wasInRef.current = true;
                    setIsUserBlocked(false);
                    setIsAuthenticated(true);
                }
            } else {
                setIsAuthenticated(false);
            }
            setIsLoading(false); // End loading state
        });
        return () => unsubscribe(); // Clean up subscription
    }, []);

    if (isLoading) {
        return <Loader fullScreen />; // Show the loader while checking the authentication state
    }

    if (!isAuthenticated || isUserBlocked) {
        // console.log("protectedroute user signed out or blocked");
        const currentPath = location.pathname + location.search;

        if (currentPath.includes('/dashboard/domains/uptime-domain')) {
            const redirectUrl = `/?redirect=${encodeURIComponent(currentPath)}`;
            localStorage.setItem(PATH_REDIRECT_KEY, currentPath);
            return <Navigate to={redirectUrl} replace />;
        }

        /*
         * EVERY deep link opened while signed out comes back after sign-in, not
         * only uptime ones: an emailed "View job" or "Top up credits", a
         * bookmark, a link opened on a new device all landed on Statistics. The
         * login flow passes through a protected page, which reads this below.
         */
        if (!wasInRef.current && !isUserBlocked && keepsDeepLink(currentPath)) {
            try { localStorage.setItem(PATH_REDIRECT_KEY, keepPath(currentPath)); } catch (_) { /* storage blocked */ }
        }

        return <Navigate to="/" replace />;
    }

    // If authenticated and not blocked, check for redirectAfterLogin and redirect if needed
    const stored = localStorage.getItem(PATH_REDIRECT_KEY);
    if (stored) {
        localStorage.removeItem(PATH_REDIRECT_KEY);
        const redirectUrl = safeInAppPath(keptPath(stored));
        if (redirectUrl && redirectUrl !== location.pathname + location.search) {
            return <Navigate to={redirectUrl} replace />;
        }
    }

    return <Dashboard />;
};

export default ProtectedRoute;
